import GuideLayout from "./GuideLayout";
import { BoundingBoxFigure, EpsAnatomyFigure } from "./GuideFigures";

const SAMPLE_EPS = `%!PS-Adobe-3.0 EPSF-3.0
%%BoundingBox: 0 0 200 100
%%HiResBoundingBox: 0.0 0.0 200.0 100.0
%%Creator: EPS Viewer example
%%Title: Blue rectangle
%%CreationDate: 2026-08-01
%%EndComments

newpath
20 20 moveto
180 20 lineto
180 80 lineto
20 80 lineto
closepath
0 0 1 setrgbcolor
fill

showpage
%%EOF`;

export default function EpsFileFormatExplained() {
  return (
    <GuideLayout
      title="The EPS File Format Explained (Technical Overview)"
      description="A technical look at Encapsulated PostScript: the DSC header, the bounding box, embedded previews, the binary DOS header, why EPS has no transparency, and how it relates to PostScript and PDF."
      path="/guides/eps-file-format-explained"
      updated="August 1, 2026"
      related={["/guides/what-is-an-eps-file", "/guides/eps-vs-svg-vs-ai-vs-pdf", "/guides/fix-eps-file-problems"]}
    >
      <p className="lead">
        Most EPS frustrations — blurry previews, clipped edges, flattened shadows,
        substituted fonts — stop being mysterious once you know how the format is put
        together. This is a deeper look at what is actually inside an <code>.eps</code>{" "}
        file and why it behaves as it does.
      </p>

      <h2>EPS is a program, not an image</h2>
      <p>
        PostScript is a full programming language Adobe released in 1984 to describe
        printed pages. It is stack-based and postfix, so operands come before operators:
        you push numbers onto a stack and then name the operation. <code>20 20 moveto</code>{" "}
        pushes two coordinates and then moves the drawing point there.
      </p>
      <p>
        An EPS file is a single, self-contained PostScript program, <em>encapsulated</em>{" "}
        so it can be dropped inside another document as one reusable graphic instead of
        describing an entire multi-page job. That is the whole idea behind the name.
      </p>
      <p>
        This also explains the format&apos;s defining inconvenience: displaying an EPS means
        executing a program, which requires a PostScript interpreter such as Ghostscript.
        It is why no operating system opens EPS out of the box, and why an EPS is
        fundamentally different from a PNG.
      </p>

      <EpsAnatomyFigure />

      <h2>A complete EPS file, annotated</h2>
      <p>
        Here is a valid EPS in its entirety. It draws a blue rectangle, and every line is
        readable text:
      </p>
      <pre><code>{SAMPLE_EPS}</code></pre>
      <p>Reading it top to bottom:</p>
      <ul>
        <li><code>%!PS-Adobe-3.0 EPSF-3.0</code> — the magic first line. <code>EPSF-3.0</code> is what marks this as encapsulated rather than a plain PostScript document. If this line is missing, most tools reject the file.</li>
        <li><code>%%BoundingBox</code> — the artwork extents. More on this below.</li>
        <li><code>%%Creator</code>, <code>%%Title</code>, <code>%%CreationDate</code> — metadata. Opening a mystery EPS in a text editor and reading <code>%%Creator</code> often tells you which application produced it.</li>
        <li><code>%%EndComments</code> — end of the header block.</li>
        <li>The drawing itself — build a path with <code>moveto</code> and <code>lineto</code>, close it, set a colour, fill it.</li>
        <li><code>showpage</code> — in a print job this ejects the page. Encapsulated inside another document it should do nothing, which is one of the special rules EPS imposes.</li>
      </ul>
      <p>
        Lines starting with <code>%%</code> are DSC comments — the Document Structuring
        Conventions. They are comments as far as the language is concerned, but host
        applications read them as metadata. That dual nature is why a single wrong comment
        can break placement while the artwork still renders perfectly.
      </p>

      <h2>The bounding box</h2>
      <p>
        <code>%%BoundingBox: llx lly urx ury</code> gives the lower-left and upper-right
        corners in <strong>points</strong>, where one point is 1/72 inch. The example above
        declares artwork 200×100 points, or roughly 70×35 mm.
      </p>
      <BoundingBoxFigure />
      <p>
        Host applications trust this line completely. They reserve exactly that much space
        and scale accordingly, which produces two familiar problems:
      </p>
      <ul>
        <li><strong>Clipped artwork</strong> when the box is too small — typically because a thick stroke, glow or shadow extends past the declared bounds. Strokes are centred on the path, so half the stroke width sits outside it.</li>
        <li><strong>Unexpected white space</strong> when the box is too large.</li>
      </ul>
      <p>
        The values must be integers, which is why <code>%%HiResBoundingBox</code> was added
        for sub-point precision. When the two disagree, different applications pick
        different ones — a classic source of a graphic that positions correctly in one
        program and not another.
      </p>

      <h2>Embedded previews</h2>
      <p>
        Since raw PostScript cannot be shown without an interpreter, EPS files often carry
        a small preview bitmap so layout software can display something immediately.
        Several formats exist:
      </p>
      <div className="table-wrap">
        <table className="tbl">
          <thead><tr><th>Preview type</th><th>Platform</th><th>Notes</th></tr></thead>
          <tbody>
            <tr><td>TIFF</td><td>Windows / cross-platform</td><td>The most common today</td></tr>
            <tr><td>WMF</td><td>Windows</td><td>Windows Metafile</td></tr>
            <tr><td>PICT</td><td>Classic Mac OS</td><td>Largely historical</td></tr>
            <tr><td>EPSI</td><td>Any</td><td>ASCII hex, so the file stays plain text</td></tr>
            <tr><td>None</td><td>—</td><td>Renders correctly but shows a grey or white box until interpreted</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        These previews are usually 72 DPI. <strong>This is the real answer to &quot;why does
        my EPS look blurry?&quot;</strong> — you are looking at the thumbnail, not the
        artwork. It prints perfectly sharp, because the printer runs the PostScript.
      </p>

      <h2>The binary DOS header</h2>
      <p>
        Some EPS files are not plain text at all. The DOS variant wraps everything in a
        30-byte binary header beginning with the magic bytes{" "}
        <code>C5 D0 D3 C6</code>, followed by byte offsets and lengths for the PostScript
        section and any TIFF or WMF preview.
      </p>
      <p>
        If you open an EPS in a text editor and see binary garbage before the{" "}
        <code>%!PS-Adobe</code> line, this is why. The file is not corrupt — it is a
        container. It also explains why naively editing an EPS in a text editor can break
        it: the header records byte offsets that your edit silently invalidates.
      </p>

      <h2>What EPS is not allowed to do</h2>
      <p>
        Because an EPS runs inside someone else&apos;s document, it is forbidden from
        touching global state. Operators such as <code>setpagedevice</code>,{" "}
        <code>initgraphics</code>, <code>erasepage</code>, <code>initclip</code> and{" "}
        <code>quit</code> are all off-limits — an encapsulated graphic that reset the clip
        path or wiped the page would destroy the document hosting it.
      </p>
      <p>
        Files that violate these rules often render fine standalone and then misbehave once
        placed, which is among the harder EPS problems to diagnose.
      </p>

      <h2>Vector, raster, or both</h2>
      <p>
        EPS is primarily vector, but the <code>image</code> and <code>colorimage</code>{" "}
        operators embed raster data directly. A pure-vector EPS scales infinitely; one
        containing a 72-DPI photograph is limited by that photograph regardless of the
        wrapper. This is why &quot;it is an EPS, so it must be scalable&quot; is not always
        true — see <a href="/guides/vector-vs-raster">vector vs raster</a>.
      </p>

      <h2>Why EPS has no transparency</h2>
      <p>
        The PostScript imaging model predates modern compositing. Painting is opaque: each
        operation covers what is beneath it. There is no alpha channel and no blend modes.
      </p>
      <p>
        So when artwork with drop shadows, glows or semi-transparent fills is exported to
        EPS, those effects must be <strong>flattened</strong> — the renderer computes the
        composited result and bakes it into opaque shapes, often slicing the artwork into
        many small pieces. That is why soft shadows sometimes show faint seams or a
        rectangular edge, and why the effects are no longer editable afterwards.
      </p>

      <h2>Fonts</h2>
      <p>
        Text can be handled two ways. Fonts may be <strong>embedded</strong>, declared via{" "}
        <code>%%DocumentSuppliedFonts</code>, which makes the file bigger but self-sufficient.
        Or text may be <strong>outlined</strong> — converted to plain vector paths before
        export. Outlined text always renders correctly but can never be edited again.
      </p>
      <p>
        If neither happened, the file merely <em>names</em> the fonts it wants via{" "}
        <code>%%DocumentNeededFonts</code>, and any machine lacking them substitutes
        something else. That is the whole explanation for text that shifts, re-flows or
        turns into boxes on another computer.
      </p>

      <h2>EPS and PDF</h2>
      <p>
        PDF grew directly out of PostScript and superseded EPS for nearly every purpose. It
        adds native transparency, reliable font embedding, real compression, multi-page
        support, and — critically — it is declarative rather than a program to execute,
        making it far faster and safer to render.
      </p>
      <p>
        That last point matters practically: PostScript interpreters have a long history of
        security vulnerabilities, which is why Microsoft Office removed EPS import and Apple
        dropped EPS support from Preview. Converting to{" "}
        <a href="/guides/convert-eps-to-pdf">PDF</a> is nearly lossless, since both share the
        same imaging heritage. See{" "}
        <a href="/guides/eps-vs-svg-vs-ai-vs-pdf">EPS vs SVG vs AI vs PDF</a> for a full
        comparison.
      </p>

      <h2>Frequently asked questions</h2>
      <dl className="guide-faq">
        <dt>Is EPS the same as PostScript (.ps)?</dt>
        <dd>Closely related but not identical. A .ps file describes a complete document to print; an EPS is a single graphic with a bounding box and extra restrictions, meant to be embedded in another document.</dd>
        <dt>Can I read an EPS in a text editor?</dt>
        <dd>Usually the header and drawing code are plain text. Embedded previews, binary DOS headers and compressed image data are not readable, and editing around them can corrupt the file.</dd>
        <dt>What does the EPSF-3.0 in the first line mean?</dt>
        <dd>It declares conformance to version 3.0 of the Encapsulated PostScript specification. Without it, applications treat the file as ordinary PostScript rather than an embeddable graphic.</dd>
        <dt>Why is my EPS file so large?</dt>
        <dd>Usually an embedded high-resolution bitmap, an uncompressed preview, or extremely complex vector artwork with tens of thousands of paths.</dd>
        <dt>Is EPS obsolete?</dt>
        <dd>Effectively superseded, but far from gone. Logo archives, print templates and stock libraries hold decades of EPS files, so it remains very much in circulation.</dd>
        <dt>What is a point in an EPS bounding box?</dt>
        <dd>1/72 of an inch, the traditional typographic unit PostScript uses throughout. A 72×72 point graphic is exactly one inch square.</dd>
      </dl>
    </GuideLayout>
  );
}
